import { ConfigStateService } from '@abp/ng.core';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import {
  LocalizationSettingsOptionsDto,
  TimeZoneOptionDto,
} from 'src/core/models/settings/localization/localization-settings-options.dto';
import { LocalizationSettingsDto } from 'src/core/models/settings/localization/localization-settings.dto';

const LOCALIZATION_SETTINGS = `${API_PREFIX}/localization-settings`;

/**
 * Reads and persists the tenant scoped display preferences of
 * Administration > Settings > Localization.
 */
@Injectable({ providedIn: 'root' })
export class LocalizationSettingsService {
  private readonly http = inject(HttpClient);
  private readonly configState = inject(ConfigStateService);

  /** Effective preferences of the current tenant, falling back to registered defaults. */
  get(): Observable<LocalizationSettingsDto> {
    return this.http.get<LocalizationSettingsDto>(resolveUrl(LOCALIZATION_SETTINGS));
  }

  /** Selectable languages for the dropdown. */
  getOptions(): Observable<LocalizationSettingsOptionsDto> {
    return this.http.get<LocalizationSettingsOptionsDto>(resolveUrl(`${LOCALIZATION_SETTINGS}/options`));
  }

  getTimeZones(): Observable<TimeZoneOptionDto[]> {
    return this.http.get<TimeZoneOptionDto[]>(
      resolveUrl(`${LOCALIZATION_SETTINGS}/time-zones`)
    );
  }

  /**
   * Replaces the stored preferences and refreshes the ABP application state so the new values
   * are visible to every consumer without a page reload. The settings are a single tenant-scoped
   * resource, so the writer is a PUT to the reader's URL — the backend's route for `UpdateAsync`.
   */
  save(settings: LocalizationSettingsDto): Observable<LocalizationSettingsDto> {
    return this.http
      .put<LocalizationSettingsDto>(resolveUrl(LOCALIZATION_SETTINGS), settings)
      .pipe(
        switchMap(() => this.configState.refreshAppState()),
        switchMap(() => this.get())
      );
  }
}
